import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core'
import { ModalHeaderComponent } from '../modal-header/modal-header.component'
import { ModalFooterComponent } from '../modal-footer/modal-footer.component'
import { ModalBodyComponent } from '../modal-body/modal-body.component'
import { LabeledPropertyComponent } from '../../labeled-property/labeled-property.component'
import { combineLatest, filter, map, Subscription, switchMap, tap } from 'rxjs'
import { ServicePointIdReference } from 'src/app/shared/models/referenceData'
import { IntervalUsageService } from '../../interval-usage/interval-usage.service'
import { IntervalUsage } from 'src/app/shared/models/intervalUsage'
import { ServicePointSelectorComponent } from '../../service-point-selector/service-point-selector.component'
import { DatePickerButtonComponent } from '../../date-picker-button/date-picker-button.component'
import { UpdateIntervalUsageTableComponent } from '../../table/update-interval-usage-table.component'
import { AsyncPipe } from '@angular/common'
import { TableEditableDataService } from '../../table/services/table-editable-data-service'
import { differenceInHours, setHours, startOfDay } from 'date-fns'
import { est, formatAsUTC } from 'src/app/shared/utilities/date'
import { toObservable } from '@angular/core/rxjs-interop'
import { isEmpty } from 'lodash'

type ModalAction = 'Create' | 'Update'

@Component({
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    LabeledPropertyComponent,
    ServicePointSelectorComponent,
    DatePickerButtonComponent,
    UpdateIntervalUsageTableComponent,
    UpdateIntervalUsageTableComponent,
    AsyncPipe,
  ],
  selector: 'app-update-interval-usage-modal',
  template: `
    <div app-modal-header>{{ action() }} Interval Usage</div>
    <div app-modal-body>
      <div app-labeled-property label="Service Point">
        <div
            app-service-point-selector
            [slim]="true"
            [value]="servicePointIdRef()"
            (valueChange)="servicePointIdRef.set($event)"
            [config]="{allowMultiple: false}"
        >
        </div>
      </div>
      <div app-labeled-property label="Market Interval">
        <app-date-picker-button
            [slim]="true"
            [value]="marketInterval()" 
            (valueChange)="marketInterval.set($event)"
        >
        </app-date-picker-button>
      </div>
      <app-update-interval-usage-table
          [data]="(data$ | async) ?? []"
          [loading]="loading()"
      />
    </div>
    <div app-modal-footer>
      <button class="action-btn" (click)="onCancel()()">Cancel</button>
      <button class="action-btn primary" [disabled]="!hasChanges()" (click)="update()">{{ action() }}</button>
    </div>
  `,
})
export class UpdateIntervalUsageModalComponent implements OnInit, OnDestroy {
  private readonly intervalUsageService = inject(IntervalUsageService)
  private readonly editableDataService = inject(TableEditableDataService)
  private readonly subscription = new Subscription()

  protected readonly marketInterval = signal<Date | undefined>(undefined)
  protected readonly servicePointIdRef = signal<ServicePointIdReference | null>(null)
  protected readonly hasChanges = signal(false)
  protected readonly loading = signal(false)
  protected readonly action = signal<'Create' | 'Update'>('Update')
  protected readonly onCancel = signal<() => void>(() => void 0)
  protected readonly onUpdate = signal<(data: Partial<IntervalUsage>[]) => void>(() => void 0)

  protected readonly data$ = combineLatest([
    toObservable(this.servicePointIdRef),
    toObservable(this.marketInterval),
  ]).pipe(
    filter(([servicePointRef, marketInterval]) => Boolean(servicePointRef && marketInterval)),
    tap(() => {
      this.loading.set(true)
    }),
    switchMap(([servicePointRef, marketInterval]) =>
      this.intervalUsageService.getIntervalUsage(
        { pageSize: 24, pageNumber: 1 },
        [
          {
            name: 'service_point_uid',
            values: [{ value: servicePointRef!.service_point_uid }],
          },
          {
            name: 'market_interval',
            values: [
              {
                operation: 'between=',
                value: [
                  formatAsUTC(setHours(startOfDay(est(marketInterval)), 0)),
                  formatAsUTC(setHours(startOfDay(est(marketInterval)), 23)),
                ].join(','),
              },
            ],
          },
        ],
        [{ name: 'market_interval' }],
      ),
    ),
    map((res) =>
      res.results.sort((a, b) => {
        return differenceInHours(a.market_interval, b.market_interval)
      }),
    ),
    tap(() => {
      this.loading.set(false)
    }),
  )

  ngOnInit() {
    this.subscription.add(this.editableDataService.data$.subscribe(data => {
      this.hasChanges.set(!isEmpty(data))
    }))
  }

  ngOnDestroy() {
    this.subscription.unsubscribe()
  }

  init(overrides: {
    action: ModalAction, 
    data?: IntervalUsage,
    onCancel?: () => void
    onUpdate?: (data: Partial<IntervalUsage>[]) => void
  }) {
    this.action.set(overrides.action)
    if (overrides.data) {
      this.marketInterval.set(overrides.data.market_interval)
      this.servicePointIdRef.set(overrides.data)
    }
    this.onCancel.update(v => overrides.onCancel ?? v)
    this.onUpdate.update(v => overrides.onUpdate ?? v)
  }

  protected async update() {
    const updates = Object.entries(this.editableDataService.getData() ?? {}).map(
      ([k, v]): Partial<IntervalUsage> => {
        const [service_point_uid, market_interval] = k.split('|')
        return {
          service_point_uid,
          market_interval: new Date(market_interval),
          measurement_value: Number(v as string),
        }
      },
    )
    this.onUpdate()(updates)
  }
}
